import { useTranslation } from 'react-i18next';

import { LegalEntityResponse } from '@/shared/api/account';
import { Form } from '@/shared/ui/Form';
import { ControlledSelect } from '@/shared/ui/Select';
import { ControlledTextField } from '@/shared/ui/TextField';
import { Box, MenuItem, Stack } from '@/shared/ui';
import { Dialog, TDialogBase } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/lib/Button';

import { legalEntitySchema } from '../model/schemas';
import { useUpdateLegalEntity } from '../model/useUpdateLegalEntity';

type LegalEntityFormValues = {
  entityType: 'ip' | 'ooo';
  name: string;
  inn: string;
  kpp?: string;
  ogrn?: string;
  legalAddress?: string;
};

type LegalEntityDialogProps = TDialogBase & {
  legalEntity?: LegalEntityResponse;
};

const LEGAL_ENTITY_FORM_ID = 'legal-entity-form';

export const LegalEntityDialog = ({
  open,
  onClose,
  legalEntity,
}: LegalEntityDialogProps) => {
  const { t } = useTranslation();
  const { updateLegalEntity, ...mutation } = useUpdateLegalEntity();

  const handleClose = () => {
    onClose?.();
  };

  const defaultValues: LegalEntityFormValues = {
    entityType: legalEntity?.entity_type ?? 'ooo',
    name: legalEntity?.name ?? '',
    inn: legalEntity?.inn ?? '',
    kpp: legalEntity?.kpp ?? '',
    ogrn: legalEntity?.ogrn ?? '',
    legalAddress: legalEntity?.legal_address ?? '',
  };

  const handleSubmit = async (data: LegalEntityFormValues) => {
    await updateLegalEntity({
      ...data,
      kpp: data.entityType === 'ooo' ? data.kpp : undefined,
      ogrn: data.ogrn || undefined,
      legalAddress: data.legalAddress || undefined,
    });
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('settings:legalEntity.dialogTitle')}
      leftButtonSlot={
        <Button variant="outlined" onClick={handleClose}>
          {t('common:buttons.cancel')}
        </Button>
      }
      rightButtonSlot={
        <Button
          variant="contained"
          type="submit"
          form={LEGAL_ENTITY_FORM_ID}
          loading={mutation.isPending}
        >
          {t('common:buttons.save')}
        </Button>
      }
    >
      <Form<LegalEntityFormValues>
        key={`${legalEntity?.entity_type ?? 'empty'}-${legalEntity?.inn ?? ''}-${open}`}
        id={LEGAL_ENTITY_FORM_ID}
        defaultValues={defaultValues}
        schema={legalEntitySchema(t)}
        mode="onChange"
        onSubmit={handleSubmit}
        mutation={mutation}
        sx={{ width: '520px' }}
      >
        {({ control, watch }) => {
          const entityType = watch('entityType');
          return (
            <Stack gap={2}>
              <ControlledSelect
                name="entityType"
                control={control}
                label={t('settings:legalEntity.fields.entityType')}
              >
                <MenuItem value="ooo">
                  {t('settings:legalEntity.entityTypes.ooo')}
                </MenuItem>
                <MenuItem value="ip">
                  {t('settings:legalEntity.entityTypes.ip')}
                </MenuItem>
              </ControlledSelect>
              <ControlledTextField
                name="name"
                control={control}
                label={t('settings:legalEntity.fields.name')}
              />
              <ControlledTextField
                name="inn"
                control={control}
                label={t('settings:legalEntity.fields.inn')}
              />
              {entityType === 'ooo' ? (
                <ControlledTextField
                  name="kpp"
                  control={control}
                  label={t('settings:legalEntity.fields.kpp')}
                />
              ) : null}
              <ControlledTextField
                name="ogrn"
                control={control}
                label={
                  entityType === 'ooo'
                    ? t('settings:legalEntity.fields.ogrn')
                    : t('settings:legalEntity.fields.ogrnip')
                }
              />
              <Box>
                <ControlledTextField
                  name="legalAddress"
                  control={control}
                  label={t('settings:legalEntity.fields.legalAddress')}
                  multiline
                  minRows={3}
                />
              </Box>
            </Stack>
          );
        }}
      </Form>
    </Dialog>
  );
};
